import { Controller } from "@hotwired/stimulus"

// Configuration for dropdown organization
const DROPDOWN_CONFIG = {
  GROUPS: {
    UNIT: [
      { id: "assigned_unit", label: "Assigned", order: 1 },
      { id: "special_options", label: "", order: 2 },
      { id: "available_units", label: "", order: 3 }
    ],
    BUILDING: [
      { id: "assigned_building", label: "Assigned", order: 1 },
      { id: "available_buildings", label: "", order: 2 }
    ]
  },
  OPTIONS: {
    COMMON_LOCK: { text: "Common Lock", value: "common_lock" },
    UNASSIGNED: { text: "Unassigned", value: "unassigned" }
  }
}

// Shared cache across all controller instances
const UNITS_CACHE = new Map()

export default class extends Controller {
  static targets = ["buildingDropdown", "doorDropdown", "row"]
  static values = {
    loadUnitsUrl: String,
    originalUnitId: String,
    originalBuildingId: String
  }

  connect() {
    this.initializeAllDropdowns()
    this.handleSubmitButtonStatus()
  }

  // Initialize all dropdowns on rows
  initializeAllDropdowns() {
    this.rowTargets.forEach(row => this.initializeRowDropdowns(row))
  }

  // Initialize dropdowns for a single row
  initializeRowDropdowns(row) {
    const doorDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='doorDropdown']")
    const buildingDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='buildingDropdown']")
    const rowAttributes = this.getRowAttributes(row)

    // Initialize door dropdown first to be ready when building changes
    if (doorDropdown && !doorDropdown.selectize) {
      this.initializeUnitDropdown(doorDropdown, rowAttributes, row)
    }

    // Initialize building dropdown
    if (buildingDropdown && !buildingDropdown.selectize) {
      this.initializeBuildingDropdown(buildingDropdown, rowAttributes, row)
    }

    // Set initial door dropdown state
    this.updateDoorDropdownState(row)
  }

  // Get row attributes needed for initialization
  getRowAttributes(row) {
    // Find the original unit ID and building ID values
    let originalUnitId = row.getAttribute("data-remote-lock--settings--selected-doors-original-unit-id-value")
    const doorDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='doorDropdown']")

    if (!originalUnitId && doorDropdown) {
      originalUnitId = doorDropdown.getAttribute("data-remote-lock--settings--selected-doors-original-assignment-value")
      // Copy it to the row for consistency if found in the dropdown
      if (originalUnitId) {
        row.setAttribute("data-remote-lock--settings--selected-doors-original-unit-id-value", originalUnitId)
      }
    }

    const originalBuildingId = row.getAttribute("data-remote-lock--settings--selected-doors-original-building-id-value")

    return { originalUnitId, originalBuildingId }
  }

  // Initialize building dropdown
  initializeBuildingDropdown(dropdown, rowAttributes, row) {
    $(dropdown).selectize({
      dropdownParent: "body",
      lockOptgroupOrder: true,
      hideEmptyOptgroups: false,
      render: {
        optgroup_header: (data, escape) => `<div class="optgroup-header">${escape(data.label)}</div>`
      },
      onChange: value => {
        const doorDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='doorDropdown']")
        if (doorDropdown) {
          this.updateDoorDropdownForBuilding(doorDropdown, value, row)
        }
        this.enableSubmitButton()
        this.updateDoorDropdownState(row)
      },
      onInitialize: function() {
        const { BUILDING } = DROPDOWN_CONFIG.GROUPS
        const { originalBuildingId } = rowAttributes

        // Ensure optgroups exist
        BUILDING.forEach(group => {
          if (!this.optgroups?.[group.id]) {
            this.addOptionGroup(group.id, { label: group.label, $order: group.order })
          }
        })

        // Apply appropriate optgroups to existing options
        const assignedGroupId = BUILDING[0].id
        const availableGroupId = BUILDING[1].id

        for (const key in this.options) {
          const option = this.options[key]
          const isOriginal = key === originalBuildingId

          option.optgroup = isOriginal ? assignedGroupId : availableGroupId
          option.$order = isOriginal ? 0 : null
        }

        this.refreshOptions(false)
      }
    })
  }

  // Initialize unit dropdown
  initializeUnitDropdown(dropdown, rowAttributes, row) {
    const { originalUnitId } = rowAttributes

    $(dropdown).selectize({
      dropdownParent: "body",
      lockOptgroupOrder: true,
      hideEmptyOptgroups: false,
      render: {
        optgroup_header: (data, escape) => `<div class="optgroup-header">${escape(data.label)}</div>`
      },
      onChange: () => this.enableSubmitButton(),
      onInitialize: function() {
        const self = this
        const { UNIT } = DROPDOWN_CONFIG.GROUPS
        const { COMMON_LOCK, UNASSIGNED } = DROPDOWN_CONFIG.OPTIONS

        // Ensure all optgroups exist
        UNIT.forEach(group => {
          if (!self.optgroups?.[group.id]) {
            self.addOptionGroup(group.id, { label: group.label, $order: group.order })
          }
        })

        // Determine special option groups
        const isCommonLock = originalUnitId === COMMON_LOCK.value
        const isUnassigned = originalUnitId === UNASSIGNED.value || originalUnitId === ""

        // Add special options with appropriate grouping
        self.addOption({
          ...COMMON_LOCK,
          $order: isCommonLock ? 0 : null,
          optgroup: isCommonLock ? "assigned_unit" : "special_options"
        })

        self.addOption({
          ...UNASSIGNED,
          $order: isUnassigned ? 0 : null,
          optgroup: isUnassigned ? "assigned_unit" : "special_options"
        })

        // Add original unit if it's a specific unit
        if (originalUnitId && !["common_lock", ""].includes(originalUnitId)) {
          const labelText = dropdown.selectedOptions[0]?.textContent.trim() || `Unit ${originalUnitId}`

          self.addOption({
            text: labelText,
            value: originalUnitId,
            $order: 0,
            optgroup: "assigned_unit"
          })
        }

        // Ensure all options have valid optgroups
        for (const key in self.options) {
          const option = self.options[key]
          if (!option.optgroup || !self.optgroups[option.optgroup]) {
            option.optgroup = "available_units"
          }
        }

        self.refreshOptions(false)

        // Set initial value if it exists in options
        if (originalUnitId && self.options[originalUnitId]) {
          self.setValue(originalUnitId, true)
        }
      }
    })
  }

  // Update door dropdown state (enable/disable)
  updateDoorDropdownState(row) {
    const buildingDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='buildingDropdown']")
    const doorDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='doorDropdown']")

    if (!buildingDropdown || !doorDropdown?.selectize) return

    const buildingId = buildingDropdown.value

    // Enable/disable based on building selection
    doorDropdown.selectize[buildingId ? "enable" : "disable"]()
  }

  // Handle dropdown container click event
  clickDropdown(event) {
    const containerElement = event.currentTarget
    const dropdownElement = containerElement.querySelector("select")
    if (!dropdownElement?.selectize) return

    const isBuilding = dropdownElement.name?.includes("[building_id]")
    const isDoor = dropdownElement.name?.includes("[unit_id]")
    const row = containerElement.closest("[data-remote-lock--settings--selected-doors-target='row']")

    // For building dropdowns, just open it
    if (isBuilding) {
      dropdownElement.selectize.open()
      return
    }

    // For door dropdowns, check building ID first
    if (isDoor) {
      const buildingDropdown = row.querySelector("[data-remote-lock--settings--selected-doors-target='buildingDropdown']")
      const buildingId = buildingDropdown?.value

      if (!buildingId) return // Don't open if no building selected

      this.openDoorDropdown(dropdownElement, buildingId, row)
    }
  }

  // Open door dropdown with building units
  async openDoorDropdown(doorDropdown, buildingId, row) {
    const selectize = doorDropdown.selectize
    if (!selectize) return

    // Set up basic structure
    this.ensureOptgroups(selectize, DROPDOWN_CONFIG.GROUPS.UNIT)

    // Get units for the building
    let units = []
    const cacheKey = String(buildingId)

    if (UNITS_CACHE.has(cacheKey)) {
      units = UNITS_CACHE.get(cacheKey) || []
    } else {
      const data = await this.fetchUnitsForBuilding(buildingId)
      units = data || []
    }

    // Populate units in dropdown
    this.populateUnitsInDropdown(doorDropdown, units, buildingId, row)

    // Open the dropdown
    selectize.open()
  }

  // Populate units in a door dropdown
  populateUnitsInDropdown(dropdown, units, currentBuildingId, row) {
    if (!dropdown?.selectize) return

    const selectize = dropdown.selectize
    const currentValue = selectize.getValue()
    const { originalUnitId, originalBuildingId } = this.getRowAttributes(row)
    const isOriginalBuilding = String(currentBuildingId) === String(originalBuildingId)

    // Ensure optgroups exist
    this.ensureOptgroups(selectize, DROPDOWN_CONFIG.GROUPS.UNIT)

    // Add original unit if on original building and it's a specific unit
    if (isOriginalBuilding && originalUnitId &&
        originalUnitId !== "common_lock" && originalUnitId !== "") {
      this.addOriginalUnitToDropdown(selectize, originalUnitId, units)
    }

    // Add all units to dropdown
    units.forEach(unit => {
      // Check if this is the original unit from original building
      const isOriginalUnit = isOriginalBuilding &&
                            String(unit.value) === String(originalUnitId) &&
                            originalUnitId !== "common_lock" &&
                            originalUnitId !== ""

      // Skip if original unit from different building
      if (!isOriginalBuilding && String(unit.value) === String(originalUnitId)) {
        return
      }

      const targetOptgroup = isOriginalUnit ? "assigned_unit" : "available_units"

      if (!selectize.options[unit.value]) {
        // Add new option
        selectize.addOption({
          ...unit,
          $order: isOriginalUnit ? 0 : null,
          optgroup: targetOptgroup
        })
      } else if (isOriginalUnit && selectize.options[unit.value].optgroup !== "assigned_unit") {
        // Update existing option if needed
        selectize.updateOption(unit.value, {
          ...selectize.options[unit.value],
          $order: 0,
          optgroup: "assigned_unit"
        })
      }
    })

    // Refresh and restore selection
    selectize.refreshOptions(false)
    if (currentValue && selectize.options[currentValue]) {
      selectize.setValue(currentValue, true)
    }
  }

  // Add original unit to dropdown
  addOriginalUnitToDropdown(selectize, originalUnitId, units) {
    // Find unit info in units array
    const originalUnit = units.find(u => String(u.value) === String(originalUnitId))

    selectize.addOption({
      ...originalUnit,
      $order: 0,
      optgroup: "assigned_unit"
    })
  }

  // Fetch units for a building
  async fetchUnitsForBuilding(buildingId) {
    const cacheKey = String(buildingId)

    // Return from cache if available
    if (UNITS_CACHE.has(cacheKey)) {
      return UNITS_CACHE.get(cacheKey)
    }

    // Fetch from server
    const response = await fetch(`${this.loadUnitsUrlValue}/?building_id=${buildingId}`, {
      headers: { "Accept": "application/json" }
    })
    const data = await response.json()

    // Cache result
    UNITS_CACHE.set(cacheKey, data)
    return data
  }

  // Enable the submit button
  enableSubmitButton() {
    document.dispatchEvent(new CustomEvent("remote_lock:enable-submit-button", { bubbles: true }))
  }

  // Ensure specific optgroups exist in a selectize dropdown
  ensureOptgroups(selectize, groupsConfig) {
    if (!selectize?.optgroups) {
      selectize.optgroups = {}
    }

    groupsConfig.forEach(group => {
      if (!selectize.optgroups[group.id]) {
        selectize.addOptionGroup(group.id, {
          label: group.label,
          $order: group.order
        })
      }
    })
  }

  // Update door dropdown for selected building
  async updateDoorDropdownForBuilding(doorDropdown, buildingId, row) {
    if (!doorDropdown?.selectize) return

    const selectize = doorDropdown.selectize
    const { originalUnitId, originalBuildingId } = this.getRowAttributes(row)
    const { COMMON_LOCK, UNASSIGNED } = DROPDOWN_CONFIG.OPTIONS

    // Check relationship flags
    const isCommonLockForCurrentBuilding = originalUnitId === "common_lock" &&
                                         String(originalBuildingId) === String(buildingId)
    const isOriginalBuilding = String(buildingId) === String(originalBuildingId)

    // Reset dropdown
    selectize.clear()
    selectize.clearOptions()

    // Set enable/disable state
    selectize[buildingId ? "enable" : "disable"]()

    // Set up optgroups
    this.ensureOptgroups(selectize, DROPDOWN_CONFIG.GROUPS.UNIT)

    // Add special options
    selectize.addOption({
      ...COMMON_LOCK,
      optgroup: isCommonLockForCurrentBuilding ? "assigned_unit" : "special_options",
      $order: isCommonLockForCurrentBuilding ? 0 : null
    })

    selectize.addOption({
      ...UNASSIGNED,
      optgroup: "special_options"
    })

    // Ensure all options have valid optgroups
    for (const key in selectize.options) {
      const option = selectize.options[key]
      if (!option.optgroup || !selectize.optgroups[option.optgroup]) {
        option.optgroup = "available_units"
      }
    }

    selectize.refreshOptions(false)

    // Determine which value to set and apply it
    let valueToSet = UNASSIGNED.value
    if (isCommonLockForCurrentBuilding) {
      valueToSet = "common_lock"
    } else if (isOriginalBuilding && originalUnitId !== "" && originalUnitId !== "common_lock") {
      valueToSet = originalUnitId
    }

    // Set appropriate value
    if (isOriginalBuilding && valueToSet && selectize.options[valueToSet]) {
      selectize.setValue(valueToSet, true)
    } else {
      selectize.setValue(UNASSIGNED.value, true)
    }

    // Load units if a building is selected
    if (buildingId) {
      // Get units from cache or fetch them
      let units = []
      const cacheKey = String(buildingId)

      if (UNITS_CACHE.has(cacheKey)) {
        units = UNITS_CACHE.get(cacheKey) || []
      } else {
        const data = await this.fetchUnitsForBuilding(buildingId)
        units = data || []
      }

      this.populateUnitsInDropdown(doorDropdown, units, buildingId, row)
    }
  }

  // Handle submit button status for the entire component
  handleSubmitButtonStatus() {
    // Enable submit button when any dropdown changes
    this.element.addEventListener("change", () => this.enableSubmitButton())
  }
}
